import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { MainLayout } from "@/components/layout/MainLayout";
import PromptBuilderChat from "@/components/prompt-builder/PromptBuilderChat";
import { BuilderProgressSidebar } from "@/components/prompt-builder/BuilderProgressSidebar";
import { useBuilderConversation } from "@/hooks/useBuilderConversation";
import { useAuth } from "@/hooks/useAuth";
import { useIsMobile } from "@/hooks/use-mobile";
import { supabase } from "@/integrations/supabase/client";
import { classifyError } from "@/lib/fetchWithTimeout";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { ArrowLeft } from "lucide-react";
import { type BuilderMessage, TOOL_SELECT_PREFIX } from "@/lib/promptBuilderUtils";
import type { RemixContext, DraftResume } from "@/types/promptBuilder";

interface BuilderLocationState {
  initialGoal?: string;
  remixContext?: RemixContext;
  guided?: boolean;
  returnTo?: string;
  attachmentConversationId?: string;
  /** Set by Workflow Builder v2 when launching the prompt builder for a specific step. */
  workflowReturn?: { workflowId: string; stepId: string };
}

export default function BuilderPage() {
  const { conversationId: urlConversationId } = useParams<{ conversationId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const isMobile = useIsMobile();
  const state = location.state as BuilderLocationState | null;

  const { startConversation, resumeConversation, loadConversation, abandonConversation } = useBuilderConversation();

  const [currentStep, setCurrentStep] = useState(0);
  const [chatMessages, setChatMessages] = useState<BuilderMessage[]>([]);
  const [isReady, setIsReady] = useState(false);
  const [draftResume, setDraftResume] = useState<DraftResume | undefined>(undefined);
  const [managedConversationId, setManagedConversationId] = useState<string | null>(null);
  const initRef = useRef(false);

  const returnTo = state?.returnTo || "/feed";

  // Initialize based on URL mode
  useEffect(() => {
    if (initRef.current) return;
    initRef.current = true;

    const init = async () => {
      if (urlConversationId === "new") {
        let newId: string | null = null;

        if (state?.attachmentConversationId) {
          // Reuse conversation created for file attachments on /prompt
          newId = state.attachmentConversationId;
          resumeConversation(newId);
          // Update the existing conversation with the initial goal
          if (state.initialGoal?.trim()) {
            await supabase
              .from('builder_conversations')
              .update({ initial_goal: state.initialGoal.trim() })
              .eq('id', newId);
          }
        } else {
          newId = await startConversation(state?.initialGoal?.trim() || undefined);
        }

        if (newId) {
          setManagedConversationId(newId);
          navigate(`/build/${newId}`, { replace: true, state });
        }
        setIsReady(true);
      } else if (urlConversationId) {
        // Load existing conversation
        const loaded = await loadConversation(urlConversationId);
        if (!loaded) {
          toast.error("Conversation not found");
          navigate(returnTo, { replace: true });
          return;
        }
        if (loaded.status === "completed") {
          toast.info("This conversation has already been completed");
          navigate(returnTo, { replace: true });
          return;
        }
        // Set up draft resume data for PromptBuilderChat
        setDraftResume({
          conversationId: urlConversationId,
          messages: loaded.messages.map((m) => ({
            role: m.role,
            content: m.content,
            id: m.id,
            timestamp: m.timestamp.toISOString(),
          })),
          turnCount: loaded.turnCount,
          initialGoal: loaded.initialGoal,
        });
        setIsReady(true);
      }
    };

    init();
  }, [urlConversationId, startConversation, loadConversation, navigate, state, returnTo]);

  const handleComplete = useCallback(async (assembledPrompt: string, conversationId?: string | null) => {
    // Workflow v2 short-circuit: when the prompt builder was launched from a
    // workflow step, return the finished prompt to the workflow instead of
    // scoring it inline. The workflow page will absorb the prompt via
    // location.state.workflowDraft.
    const workflowReturn = state?.workflowReturn;
    if (workflowReturn) {
      navigate(`/workflows/${workflowReturn.workflowId}`, {
        replace: true,
        state: {
          workflowDraft: {
            stepId: workflowReturn.stepId,
            promptBody: assembledPrompt,
            conversationId: conversationId ?? null,
          },
        },
      });
      return;
    }

    const remixContext = state?.remixContext;
    try {
      const { data, error } = await supabase.functions.invoke("score-submission", {
        body: {
          content: assembledPrompt,
          submission_type: "prompt_single",
          source: "app",
          conversation_id: conversationId || undefined,
        },
      });

      if (error) {
        const classified = classifyError(error);
        toast.error(classified.message);
        throw error;
      }
      if (data.error) {
        toast.error(data.error);
        throw new Error(data.error);
      }

      if (data.sensitivity_flag) {
        toast.info("This prompt contains sensitive information and has been saved privately in your library.");
      }

      // Record remix relationship if this was a remix
      if (remixContext?.originalScoreId && user?.id) {
        const { error: remixError } = await supabase
          .from("prompt_remixes")
          .insert({
            original_score_id: remixContext.originalScoreId,
            remixed_score_id: data.score_id,
            user_id: user.id,
          });

        if (remixError) {
          console.error("Failed to record remix:", remixError);
        }
      }

      const transformedResult = {
        ui: {
          ...data.analysis,
          score_id: data.score_id,
          submission_id: data.submission_id,
          is_public: !data.sensitivity_flag,
          original_score: data.original_score,
          improved_score: data.overall_score,
          user_id: user?.id,
          ...(remixContext?.originalScoreId && {
            is_remix: true,
            original_score_id: remixContext.originalScoreId,
            source_original_prompt: remixContext.originalPrompt,
          }),
        },
        telemetry: {
          tags: data.analysis?.tags || [],
        },
      };

      // Recover tool recommendations from DB for the score page
      let toolRecommendations = null;
      let recId = null;
      let selectedToolName: string | null = null;
      let preOptimizationPrompt = assembledPrompt;
      if (conversationId) {
        const { data: toolRecData } = await supabase
          .from("tool_recommendations")
          .select("*")
          .eq("conversation_id", conversationId)
          .order("created_at", { ascending: false })
          .limit(1)
          .maybeSingle();

        if (toolRecData) {
          recId = toolRecData.id;
          toolRecommendations = [
            { rank: 1, tool: toolRecData.recommended_tool, model: toolRecData.recommended_model || "", reasoning: toolRecData.reasoning },
            ...(toolRecData.second_tool ? [{ rank: 2, tool: toolRecData.second_tool, model: toolRecData.second_model || "", reasoning: toolRecData.second_reasoning || "" }] : []),
            ...(toolRecData.third_tool ? [{ rank: 3, tool: toolRecData.third_tool, model: toolRecData.third_model || "", reasoning: toolRecData.third_reasoning || "" }] : []),
          ];

          // If a tool was selected in the builder, pass the tool name and
          // pre-optimization prompt so the score page shows the compact bar
          // and uses the right base for any "change tool" re-optimization.
          if (toolRecData.user_selected_tool) {
            selectedToolName = toolRecData.user_selected_tool;
            preOptimizationPrompt = toolRecData.final_prompt || assembledPrompt;
          }
        }
      }

      navigate(`/prompt_score/${data.score_id}`, {
        state: {
          result: transformedResult,
          toolRecommendations,
          recommendationId: recId,
          originalPrompt: preOptimizationPrompt,
          selectedToolName,
        },
      });
    } catch (err) {
      console.error("Error scoring prompt:", err);
      const classified = classifyError(err);
      toast.error(classified.message);
      throw err;
    }
  }, [navigate, user?.id, state?.remixContext, state?.workflowReturn]);

  const handleClose = useCallback(() => {
    abandonConversation();
    navigate(returnTo);
  }, [abandonConversation, navigate, returnTo]);

  const handleStepChange = useCallback((step: number) => {
    setCurrentStep(step);
  }, []);

  const handleMessagesChange = useCallback((messages: BuilderMessage[]) => {
    setChatMessages(messages);
  }, []);

  // Determine props for PromptBuilderChat based on mode
  const isNewConversation = urlConversationId === "new" || (!draftResume && isReady);
  const chatInitialGoal = isNewConversation ? state?.initialGoal : undefined;
  const sidebarGoal = state?.initialGoal || draftResume?.initialGoal || chatMessages.find(m => m.role === 'user')?.content;
  const selectedTool = useMemo(() => {
    const msg = [...chatMessages].reverse().find(m => m.role === 'user' && m.content.startsWith(TOOL_SELECT_PREFIX));
    return msg ? msg.content.slice(TOOL_SELECT_PREFIX.length) : null;
  }, [chatMessages]);
  const chatRemixContext = isNewConversation ? state?.remixContext : undefined;
  const chatGuided = state?.guided ?? false;

  return (
    <MainLayout>
      <div className="flex flex-col h-[calc(100vh-0px)] lg:h-screen">
        {/* Header */}
        <div className="flex items-center gap-2 px-4 py-3 border-b border-border flex-shrink-0">
          <Button variant="ghost" size="icon" onClick={handleClose} className="h-8 w-8">
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <h2 className="font-semibold text-foreground">Build a Prompt</h2>

          {/* Mobile progress indicator */}
          {isMobile && (
            <div className="flex items-center gap-1.5 ml-auto">
              {[1, 2, 3, 4].map((step) => (
                <div
                  key={step}
                  className={`h-1.5 w-6 rounded-full transition-colors ${
                    step <= currentStep ? "bg-primary" : "bg-muted"
                  }`}
                />
              ))}
            </div>
          )}
        </div>

        {/* Main content: chat + right sidebar */}
        <div className="flex flex-1 min-h-0">
          {/* Center: Chat */}
          <div className="flex-1 flex flex-col min-w-0 min-h-0">
            {isReady && (
              <PromptBuilderChat
                initialGoal={chatInitialGoal}
                onComplete={handleComplete}
                onClose={handleClose}
                inline={true}
                remixContext={chatRemixContext}
                draftResume={draftResume}
                guided={chatGuided}
                onStepChange={handleStepChange}
                onMessagesChange={handleMessagesChange}
                existingConversationId={managedConversationId}
              />
            )}
          </div>

          {/* Right sidebar: Progress */}
          <aside className="hidden lg:block w-72 border-l border-border flex-shrink-0 min-h-0 overflow-y-auto">
            <BuilderProgressSidebar
              currentStep={currentStep}
              messages={chatMessages}
              goal={sidebarGoal}
              selectedTool={selectedTool}
            />
          </aside>
        </div>
      </div>
    </MainLayout>
  );
}
